import React, {
  useEffect,
  useRef,
  MutableRefObject,
  useCallback,
  useState,
} from "react";
import { Box } from "rebass/styled-components";
import videojs from "video.js";
import "videojs-contrib-ads";
import "videojs-ima";
import "videojs-overlay";
import "video.js/dist/video-js.css";
import "videojs-ima/dist/videojs.ima.css";
import { StyledPlayer } from "./styledComponents";
import { analytics } from "../Analytics";
import { useFullscreen } from "../Hooks/useFullscreen";
import { isMobileOnly } from "react-device-detect";

import { useAds } from "../Providers";

import { buildAdTag } from "../Utilities/Ads";

import "./index.css";
export type TSourceType = { src: string; type: string };

interface IVideoPlayerProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "onPlay"> {
  mp4Source?: string;
  hlsSource?: string;
  watchAnalyticsProperties: Record<string, unknown>;
  playAnalyticsProperties: Record<string, unknown>;
  options?: Omit<videojs.PlayerOptions, "loop">;
  refForVideoPlayer: MutableRefObject<videojs.Player | null>;
  onPlayStart?: (arg0?: videojs.Player | null) => void;
  onPlayEnd?: (arg0?: videojs.Player | null) => void;
  onPlayPause?: (arg0?: videojs.Player | null) => void;
  onPlay?: (arg0?: videojs.Player | null) => void;
  onReady?: (arg0?: videojs.Player | null) => void;
  loop?: boolean; // @IMPORTANT use this to loop instead of options.loop
  eventSrc?: string;
  wrapperId?: string;
  onAdStarted?: () => void;
  onAdCompleted?: () => void;
  duration?: number;
  volumeStorage?: "session" | "local";
  ads?: boolean;
  deferEndAd?: boolean;
}

export const VideoPlayer = ({
  mp4Source,
  hlsSource,
  options,
  refForVideoPlayer,
  onPlayStart,
  onPlayEnd,
  onPlayPause,
  onPlay,
  onReady,
  onAdStarted,
  onAdCompleted,
  watchAnalyticsProperties,
  playAnalyticsProperties,
  loop,
  eventSrc,
  wrapperId,
  volumeStorage = "session",
  duration,
  ads = false,
  deferEndAd = false,
  ...props
}: IVideoPlayerProps) => {
  const videoRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<videojs.Player | null>(null);
  const wrapperRef = useRef<HTMLElement | null>(null);
  const [secondsWatched, setSecondsWatched] = useState(0);
  const [trackedView, setTrackedView] = useState(false);
  const interval = useRef<ReturnType<typeof setInterval> | undefined>();
  const { gpt, imaStatus, config: adsConfig } = useAds();
  const [adsInitialized, setAdsInitialized] = useState(false);
  const slateSlotRef = useRef<typeof window.googletag.Slot | null>(null);
  const bugSlotRef = useRef<typeof window.googletag.Slot | null>(null);

  const { isFullscreen, toggleFullscreen } = useFullscreen(wrapperRef, {
    onEnter: () => {
      analytics.track("Button - Click", {
        ...watchAnalyticsProperties,
        locationIdentifier: "Video Player - Fullscreen",
      });
    },
  });

  const interceptFullscreenClick = useCallback(() => {
    const fullscreen = document.getElementsByClassName(
      "vjs-fullscreen-control",
    );

    if (fullscreen.length && wrapperId && !isMobileOnly) {
      const originalButton = fullscreen[0];
      const clonedFullscreen = originalButton.cloneNode(true);
      clonedFullscreen.addEventListener("click", async () => {
        const wrapper = document.getElementById(wrapperId);
        if (!wrapper) return;

        wrapperRef.current = wrapper;

        try {
          await toggleFullscreen();
        } catch (error) {
          console.error(error);
        }
      });

      originalButton.replaceWith(clonedFullscreen);
    }
  }, [wrapperId, toggleFullscreen]);

  const startTrackingSecondsWatched = useCallback(() => {
    if (!interval.current) {
      interval.current = setInterval(() => {
        setSecondsWatched((prev) => prev + 1);
      }, 1000);
    }
  }, [interval]);

  const stopTrackingSecondsWatched = useCallback(() => {
    clearInterval(interval.current);
    interval.current = undefined;
  }, [interval]);

  useEffect(() => {
    // Don't create player until IMA is ready if video ads are enabled
    if (ads && adsConfig.video.enabled && imaStatus === "loading") {
      return;
    }

    if (!playerRef.current && videoRef.current) {
      const videoElement = document.createElement("video-js");
      videoElement.classList.add("vjs-big-play-centered");
      videoRef.current.appendChild(videoElement);

      const player = videojs(videoElement, {
        controls: true,
        fluid: true,
        sources: [],
        playsinline: true,
        ...options,
      });

      const displayConfig = adsConfig.display;

      if (gpt && !adsInitialized && displayConfig.enabled && ads) {
        setAdsInitialized(true);

        const slateContainer = document.createElement("div");
        slateContainer.id = "pause-end-slate";
        slateContainer.className = "vjs-overlay vjs-slate";
        slateContainer.onclick = () => {
          player.play();
        };

        const bugContainer = document.createElement("div");
        bugContainer.id = "top-left-bug";
        bugContainer.className = "vjs-overlay vjs-bug";

        player.overlay({
          overlays: [
            {
              start: "pause",
              end: "play",
              content: slateContainer,
            },
            {
              start: "play",
              end: "pause",
              content: bugContainer,
            },
          ],
        });

        player.on("play", () => {
          const slateEl = document.getElementById("pause-end-slate");
          if (slateEl) {
            slateEl.style.display = "none";
          }

          gpt.cmd.push(() => {
            if (!bugSlotRef.current) {
              bugSlotRef.current = gpt.defineSlot(
                `/${displayConfig.networkId}/${displayConfig.bugAdUnit}`,
                [
                  [120, 60],
                  [120, 40],
                ],
                "top-left-bug",
              );

              if (bugSlotRef.current) {
                const params = new URLSearchParams(window.location.search);
                const testAds = params.get("testAds");
                if (testAds) {
                  bugSlotRef.current.setTargeting("test", testAds);
                }

                const clipGame = watchAnalyticsProperties.game as string;
                if (clipGame) {
                  bugSlotRef.current.setTargeting("game", clipGame);
                }

                const viewerSource = window.location.href.includes("iframe")
                  ? "iframe"
                  : "website";
                bugSlotRef.current.setTargeting("viewer_source", viewerSource);

                bugSlotRef.current.addService(gpt.pubads());
                gpt.display("top-left-bug");
              }
            }

            if (bugSlotRef.current) {
              gpt.pubads().refresh([bugSlotRef.current]);
            }
          });
        });

        player.on("pause", () => {
          if (player.seeking() || (player.ended() && deferEndAd)) return;

          const bugEl = document.getElementById("top-left-bug");
          if (bugEl) {
            bugEl.style.display = "none";
          }

          gpt.cmd.push(() => {
            if (!slateSlotRef.current) {
              const sizeMapping = gpt
                .sizeMapping()
                .addSize([1500, 0], [[800, 450]])
                .addSize([0, 0], [[250, 141]])
                .build();

              slateSlotRef.current = gpt.defineSlot(
                `/${displayConfig.networkId}/${displayConfig.slateAdUnit}`,
                [
                  [800, 450],
                  [250, 141],
                ],
                "pause-end-slate",
              );

              if (slateSlotRef.current) {
                slateSlotRef.current.defineSizeMapping(sizeMapping);

                const params = new URLSearchParams(window.location.search);
                const testAds = params.get("testAds");
                if (testAds) {
                  slateSlotRef.current.setTargeting("test", testAds);
                }

                const clipGame =
                  (watchAnalyticsProperties.game as string) ||
                  (watchAnalyticsProperties.clip_game as string);

                if (clipGame) {
                  slateSlotRef.current.setTargeting("game", clipGame);
                }

                const viewerSource = window.location.href.includes("iframe")
                  ? "iframe"
                  : "website";
                slateSlotRef.current.setTargeting(
                  "viewer_source",
                  viewerSource,
                );

                slateSlotRef.current.addService(gpt.pubads());
                gpt.display("pause-end-slate");
              }
            }

            const slateEl = document.getElementById("pause-end-slate");
            if (slateEl) {
              slateEl.style.display = "block";
            }

            if (slateSlotRef.current) {
              gpt.pubads().refresh([slateSlotRef.current]);
            }
          });
        });
      }

      playerRef.current = player;
    }

    if (refForVideoPlayer) {
      refForVideoPlayer.current = playerRef.current;
      if (onReady) onReady(playerRef.current);
    }
  }, [
    options,
    refForVideoPlayer,
    onReady,
    imaStatus,
    adsConfig.display,
    adsConfig.video.enabled,
    ads,
    gpt,
    adsInitialized,
    watchAnalyticsProperties,
  ]);

  useEffect(() => {
    const player = playerRef.current;
    if (!player || (ads && adsConfig.video.enabled && imaStatus === "loading"))
      return;

    player.one("ready", () => {
      if (ads && adsConfig.video.enabled && imaStatus === "ready") {
        if (typeof player.ima !== "function") {
          return;
        }

        const adTagUrl = buildAdTag({
          ...adsConfig.video,
          duration: duration ?? 0,
          clipGame: watchAnalyticsProperties.game as string,
        });

        player.ima({
          adTagUrl,
        });

        if (!player.ima.initializeAdDisplayContainer) {
          return;
        }

        player.ima.initializeAdDisplayContainer();

        player.on("adstart", () => {
          onAdStarted?.();
        });

        player.on("adend", () => {
          onAdCompleted?.();
        });
        player.on("ads-ad-skipped", () => {
          onAdCompleted?.();
        });
        player.on("aderror", () => {
          onAdCompleted?.();
        });
      }

      try {
        interceptFullscreenClick();
      } catch (error) {
        console.error(error);
      }
    });
  }, [
    imaStatus,
    interceptFullscreenClick,
    options?.autoplay,
    duration,
    onAdStarted,
    onAdCompleted,
    adsConfig.video,
    ads,
  ]);

  useEffect(() => {
    const player = playerRef.current;
    if (!player || (ads && adsConfig.video.enabled && imaStatus === "loading"))
      return;

    let source: TSourceType | null = null;

    if (mp4Source) {
      source = { src: mp4Source, type: "video/mp4" };
    } else if (hlsSource) {
      source = { src: hlsSource, type: "application/x-mpegURL" };
    }

    if (source) {
      setSecondsWatched(0);
      setTrackedView(false);
      stopTrackingSecondsWatched();

      if (options?.poster) {
        player.poster(options.poster);
      }

      if (
        ads &&
        adsConfig.video.enabled &&
        player.ima?.setContentWithAdTag &&
        player.ima?.requestAds
      ) {
        const adTagUrl = buildAdTag({
          ...adsConfig.video,
          duration: duration ?? 0,
          clipGame: watchAnalyticsProperties.game as string,
        });

        try {
          player.ima.setContentWithAdTag(source.src, adTagUrl);
          player.ima.requestAds();
        } catch (error) {
          const currentSource = player.currentSrc();
          if (currentSource !== source.src) {
            player.src(source);
            player.load();
          }
        }
      } else {
        const currentSource = player.currentSrc();
        if (currentSource !== source.src) {
          player.src(source);
          player.load();
        }
      }
    }
  }, [
    mp4Source,
    hlsSource,
    stopTrackingSecondsWatched,
    imaStatus,
    duration,
    options?.poster,
    adsConfig.video,
    ads,
  ]);

  useEffect(() => {
    const player = playerRef.current;

    return () => {
      if (player && !player.isDisposed()) {
        player.dispose();
        playerRef.current = null;
      }

      // Clean up GPT slots
      if (gpt && adsConfig.display.enabled) {
        gpt.cmd.push(() => {
          if (slateSlotRef.current) {
            gpt.destroySlots([slateSlotRef.current]);
            slateSlotRef.current = null;
          }
          if (bugSlotRef.current) {
            gpt.destroySlots([bugSlotRef.current]);
            bugSlotRef.current = null;
          }
        });
      }
    };
  }, [gpt, adsConfig.display]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const player = playerRef.current;
      if (!player) return;

      if (event.code === "Space") {
        const activeElement = document.activeElement as HTMLElement;
        if (activeElement) {
          const tagName = activeElement.tagName.toLowerCase();
          const isFormElement =
            tagName === "input" ||
            tagName === "textarea" ||
            activeElement.contentEditable === "true";

          if (isFormElement) return;
        }

        event.preventDefault();
        event.stopPropagation();

        player.paused() ? player.play() : player.pause();
      }
    };

    document.addEventListener("keydown", handleKeyDown, true);

    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, []);

  useEffect(() => {
    const player = playerRef.current;
    const onVideoPlay = () => {
      startTrackingSecondsWatched();
      if (!player?.currentTime() && onPlayStart) onPlayStart();
      if (onPlay) onPlay();

      const storage = volumeStorage === "local" ? localStorage : sessionStorage;
      const playerVolume = storage.getItem("playerVolume");
      if (playerVolume) {
        player?.volume(parseFloat(playerVolume));
      }

      analytics.track(`${eventSrc ? eventSrc : "Video Player"} - Play`, {
        ...playAnalyticsProperties,
        total_length: player?.duration(),
        file_extension: player?.currentSrc().split(".").pop(),
      });
    };

    player?.on("play", onVideoPlay);

    return () => player?.off("play", onVideoPlay);
  }, [
    onPlay,
    onPlayStart,
    playAnalyticsProperties,
    startTrackingSecondsWatched,
    eventSrc,
    volumeStorage,
  ]);

  useEffect(() => {
    const player = playerRef.current;
    const onPause = () => {
      stopTrackingSecondsWatched();
      if (onPlayPause) onPlayPause();
    };

    player?.on("pause", onPause);

    return () => player?.off("pause", onPause);
  }, [stopTrackingSecondsWatched, onPlayPause]);

  useEffect(() => {
    const player = playerRef.current;
    const onVolumeChange = () => {
      // We only want to set volume if the user intended to change it, not via autoplay settings
      if (options?.autoplay === "muted") return;

      sessionStorage.setItem(
        "playerVolume",
        player?.muted() ? "0.0" : `${player?.volume()}`,
      );
    };

    player?.on("volumechange", onVolumeChange);

    return () => player?.off("volumechange", onVolumeChange);
  }, [options?.autoplay]);

  useEffect(() => {
    const player = playerRef.current;
    const onEnded = () => {
      onPlayEnd && onPlayEnd(player);
      analytics.track(`${eventSrc ? eventSrc : "Video Player"} - End`, {
        ...watchAnalyticsProperties,
      });

      if (loop) {
        setSecondsWatched(0);
        setTrackedView(false);
        playerRef?.current?.play();
        analytics.track(`${eventSrc ? eventSrc : "Video Player"} - Looped`, {
          ...watchAnalyticsProperties,
        });
      }
    };

    player?.on("ended", onEnded);

    return () => player?.off("ended", onEnded);
  }, [loop, onPlayEnd, eventSrc, watchAnalyticsProperties]);

  useEffect(() => {
    if (secondsWatched >= 5 && !trackedView) {
      analytics.track(
        `${eventSrc ? eventSrc : "Video Player"} - Watched 5 seconds`,
        {
          ...watchAnalyticsProperties,
        },
      );
      setTrackedView(true);
      stopTrackingSecondsWatched();
    }
  }, [
    secondsWatched,
    stopTrackingSecondsWatched,
    trackedView,
    watchAnalyticsProperties,
    eventSrc,
  ]);

  return (
    <StyledPlayer {...props} data-fullscreen={isFullscreen}>
      <Box data-vjs-player>
        <Box ref={videoRef} />
      </Box>
    </StyledPlayer>
  );
};
